import React from 'react';
import { Modal, Button, Typography, Space } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useIntl } from 'react-intl';

const { Paragraph } = Typography;

// Styled Components
const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
  }

  .ant-modal-header {
    background: linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%);
    border-bottom: none;
    padding: 24px 24px 20px;
    border-radius: 16px 16px 0 0;
  }

  .ant-modal-title {
    color: #fff;
    font-size: 18px;
    font-weight: 600;
  }

  .ant-modal-close {
    display: none;
  }

  .ant-modal-body {
    padding: 32px 24px;
  }

  .ant-modal-footer {
    border-top: none;
    padding: 16px 24px 24px;
  }
`;

const ErrorIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  
  .anticon {
    font-size: 64px;
    color: #ff4d4f;
    animation: shake 0.5s ease-in-out;
  }

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-10px); }
    75% { transform: translateX(10px); }
  }
`;

const ErrorContent = styled.div`
  text-align: center;
`;

const ErrorMessage = styled(Paragraph)`
  font-size: 15px;
  color: rgba(0, 0, 0, 0.85);
  margin-bottom: 0 !important;
  line-height: 1.6;
  white-space: pre-line;
`;

const StyledButton = styled(Button)`
  height: 44px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 15px;
  min-width: 120px;
  box-shadow: 0 2px 8px rgba(22, 119, 255, 0.2);
  
  &:hover {
    box-shadow: 0 4px 12px rgba(22, 119, 255, 0.3);
  }
`;

interface ErrorModalProps {
  isOpen: boolean;
  error: string | null;
  onClose: () => void;
}

const ErrorModal: React.FC<ErrorModalProps> = ({ isOpen, error, onClose }) => {
  const intl = useIntl();
  return (
    <StyledModal
      title={intl.formatMessage({ id: "booking.errorModal.title" })}
      open={isOpen}
      onOk={onClose}
      onCancel={onClose}
      centered
      width={440}
      closable={false}
      mask={{
        closable:false
      }}
      keyboard={false}
      footer={[
        <StyledButton 
          key="submit" 
          type="primary" 
          onClick={onClose}
          block
        >
          {intl.formatMessage({ id: "booking.errorModal.closeButton" })}
        </StyledButton>,
      ]}
    >
      <Space orientation="vertical" size="large" style={{ width: '100%' }}>
        <ErrorIconWrapper>
          <CloseCircleOutlined />
        </ErrorIconWrapper>
        <ErrorContent>
          <ErrorMessage>
            {error || intl.formatMessage({ id: "booking.errorModal.defaultMessage" })}
          </ErrorMessage>
        </ErrorContent>
      </Space>
    </StyledModal>
  );
};

export default ErrorModal;